import type { PayloadAction, Update } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import type { SetDefaultHandoffPayload } from '@polyai/stream/commands/handlers/handoff/set-default/schema';
import {
  isStepReferencesHandoff,
  isUpdateTopicReferencesHandoff,
} from '@polyai/stream/store/matchers';
import {
  createHandoff,
  deleteHandoff,
  resetHandoffConfigs,
  setDefaultHandoff,
  updateHandoff,
} from '@polyai/stream/store/reducers/handoff/actions';
import type {
  Handoff,
  Handoffs,
  SipConfig,
  SipHeaders,
} from '@polyai/stream/store/reducers/handoff/types';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';
import {
  handleCreateFlowReferences,
  handleDeleteFlowReferences,
  handleDeleteFlowStepReferences,
  handleImportFlowReferences,
  handleStepReferenceUpdates,
  handleTopicReferenceUpdates,
} from '@polyai/stream/store/utils/reference-helpers';

import type { References } from '../../references';
import { createReferenceAdapter } from '../../references';
import { getDefaultDate } from '../../utils/date';
import { createFlow, deleteFlow, deleteFlowStep, importFlow } from '../flows';
import { selectActiveHandoffs } from './selectors';

const handoffAdapter = createEntityAdapter<Handoff>();
const handoffSelectors = handoffAdapter.getSelectors();

const handoffReferences = createReferenceAdapter(['topics', 'flowSteps']);

const initialState = {
  handoffs: handoffAdapter.getInitialState(),
} satisfies Handoffs;

const slice = createSlice({
  name: 'handoff',
  initialState,
  reducers: {},
  //  all reducers are here because actions are defined externally
  extraReducers: builder => {
    //  main slice reducers
    builder.addCase(createHandoff, (state, action) => {
      const handoff: Handoff = {
        ...action.payload,
        sipConfig: action.payload.sipConfig as SipConfig,
        sipHeaders: action.payload.sipHeaders as SipHeaders,
        references: action.payload.references as References<
          'topics' | 'flowSteps'
        >,
        isDefault: false,
        //  these are updated in the assignMetadata call below
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      handoffAdapter.addOne(state.handoffs, assignMetadata(action, handoff));
    });

    builder.addCase(updateHandoff, (state, action) => {
      const handoff = handoffSelectors.selectById(
        state.handoffs,
        action.payload.id,
      );
      if (handoff) {
        const updatedHandoff = assignDefinedFields(
          { ...handoff },
          {
            ...action.payload,
            sipConfig: action.payload.sipConfig as SipConfig,
          },
        );

        handoffAdapter.upsertOne(
          state.handoffs,
          assignMetadata(action, {
            ...updatedHandoff,
            updatedAt: undefined,
            updatedBy: undefined,
          }),
        );
      }
    });
    builder.addCase(deleteHandoff, (state, action) => {
      const handoff = handoffSelectors.selectById(
        state.handoffs,
        action.payload.id,
      );

      const newHandoff = {
        ...handoff,
        active: false,
      };
      assignMetadata(action, newHandoff);
      handoffAdapter.upsertOne(state.handoffs, newHandoff);
    });

    builder.addCase(
      setDefaultHandoff,
      (state, action: PayloadAction<SetDefaultHandoffPayload>) => {
        const handoff = handoffSelectors.selectById(
          state.handoffs,
          action.payload.id,
        );
        const previousDefaultHandoff = handoffSelectors
          .selectAll(state.handoffs)
          .filter(h => h.isDefault);

        previousDefaultHandoff.forEach(h => {
          handoffAdapter.upsertOne(state.handoffs, {
            ...h,
            isDefault: false,
          });
        });

        handoffAdapter.upsertOne(state.handoffs, {
          ...handoff,
          isDefault: true,
        });
        //  need to copy since readonly from selector
        assignMetadata(action, { ...handoff });

        previousDefaultHandoff.forEach(h => {
          handoffAdapter.upsertOne(state.handoffs, {
            ...h,
            isDefault: false,
          });
        });
      },
    );

    builder.addCase(resetHandoffConfigs, (state, action) => {
      const defaultSipConfig: SipConfig = {
        config: { $case: 'bye', value: {} },
      };
      const defaultSipHeaders: SipHeaders = { headers: [] };
      const sipConfig = (action.payload.sipConfig ??
        defaultSipConfig) as SipConfig;

      const sipHeaders = (action.payload.sipHeaders ??
        defaultSipHeaders) as SipHeaders;

      const updates: Update<Handoff, string>[] = state.handoffs.ids.map(id => {
        const handoff = state.handoffs.entities[id];
        const updatedHandoff = {
          ...handoff,
          sipConfig,
          sipHeaders,
        };
        return {
          id,
          changes: assignMetadata(action, updatedHandoff),
        };
      });

      handoffAdapter.updateMany(state.handoffs, updates);
    });

    //  Flow references - using shared utility
    builder.addCase(createFlow, (state, action) => {
      handleCreateFlowReferences(
        action.payload.steps,
        'handoff',
        action.payload.id,
        handoffReferences.addReference,
        state.handoffs,
      );
    });

    builder.addCase(importFlow, (state, action) => {
      if (action.payload.handoffs.length) {
        const now = getDefaultDate();
        handoffAdapter.addMany(
          state.handoffs,
          action.payload.handoffs.map(h =>
            assignMetadata(action, {
              ...h,
              //  these are updated in the assignMetadata call
              createdAt: now,
              createdBy: 'system',
              updatedAt: now,
              updatedBy: 'system',
              isDefault: false,
            }),
          ),
        );
      }
      handleImportFlowReferences(
        action.payload.steps,
        'handoff',
        action.payload.flowId,
        handoffReferences.addReference,
        state.handoffs,
      );
    });

    builder.addCase(deleteFlow, (state, action) => {
      handleDeleteFlowReferences(
        handoffSelectors.selectAll(state.handoffs),
        action.payload.flowId,
        handoffReferences.removeReferencePrefix,
        state.handoffs,
      );
    });

    builder.addCase(deleteFlowStep, (state, action) => {
      handleDeleteFlowStepReferences(
        handoffSelectors.selectAll(state.handoffs),
        action.payload.flowId,
        action.payload.stepId,
        handoffReferences.removeReference,
        state.handoffs,
      );
    });

    //  side-effect reducers
    builder.addMatcher(isUpdateTopicReferencesHandoff, (state, action) => {
      const references = action.payload.references?.handoff ?? {};
      const handoffs = Object.keys(references)
        .map(ref => {
          return state.handoffs.entities[ref];
        })
        .filter(h => h !== undefined);

      for (const handoff of handoffs) {
        handoffReferences.addReference(
          state.handoffs,
          handoff.id,
          'topics',
          action.payload.id,
        );
      }
    });

    //  side-effect reducers - using shared utility
    builder.addMatcher(isUpdateTopicReferencesHandoff, (state, action) => {
      handleTopicReferenceUpdates(
        state.handoffs.entities,
        action.payload.references?.handoff ?? {},
        action.payload.id,
        handoffReferences.addReference,
        handoffReferences.removeReference,
        state.handoffs,
      );
    });

    //  Flow step references - using shared utility
    builder.addMatcher(isStepReferencesHandoff, (state, action) => {
      handleStepReferenceUpdates(
        state.handoffs.entities,
        action.payload.step?.references?.handoff ?? {},
        action.payload.flowId,
        action.payload.step.id,
        handoffReferences.addReference,
        handoffReferences.removeReference,
        state.handoffs,
      );
    });
  },
});

export type { Handoff };

export { handoffAdapter };

export { handoffSelectors, selectActiveHandoffs };

export {
  createHandoff,
  deleteHandoff,
  handoffReferences,
  resetHandoffConfigs,
  setDefaultHandoff,
  updateHandoff,
};
export default slice.reducer;
